/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Loading Splash Screen Component
 *
 * Full-screen loading overlay with logo and animated loader.
 * Used for initial app loading or major transitions.
 * Custom CSS-based ScaleLoader animation for better performance.
 */

import React from "react";
import "./loadingSplash.css";
import { STATIC_ASSETS } from "@/constants/staticAssets";

const LOADER_BARS = [0, 1, 2, 3, 4] as const;

interface LoadingSplashProps {
  logoSrc?: string;
  logoAlt?: string;
  /**
   * Spoken to screen readers while the page waits, e.g. "Logging you in…".
   * A page that is only a step on the way somewhere else (the sign-in return
   * pages, the forum hand-off, sign-out) has nothing else on screen to say
   * what is happening, so it passes one. Without it the overlay stays silent,
   * which is right for an ordinary page load.
   */
  statusLabel?: string;
}

const LoadingSplash: React.FC<LoadingSplashProps> = ({
  logoSrc = STATIC_ASSETS.logoSvg,
  logoAlt = "Logo",
  statusLabel,
}) => {
  return (
    <div
      className="loading-splash-overlay"
      role={statusLabel ? "status" : undefined}
      aria-label={statusLabel}
    >
      <div className="loading-splash-content">
        <div>
          <img src={logoSrc} alt={logoAlt} loading="eager" />
        </div>
        <div className="loading-splash-bars">
          {LOADER_BARS.map((index) => (
            <div
              key={index}
              className="loading-splash-bar"
              // Dynamic stagger via CSS custom property — avoids a static
              // CSS class per bar index while keeping inline styles minimal.
              style={
                { "--bar-delay": `${index * 0.1}s` } as React.CSSProperties
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoadingSplash;
